import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import { PaymentDrawer, PaymentDetails } from "../../components/PaymentDrawer";
import {
  ArrowLeft,
  CreditCard,
  Truck,
  Shield,
  CheckCircle2,
  Lock,
  Tag,
  Star,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getBusinessRules } from "../../services/businessRules.api";
import type { NewModel } from "./BrowseModels";

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

interface LocationState {
  model?: NewModel;
  tradeInValue?: number;
}

export function Checkout() {
  const { t } = useTranslation('upgrade');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const model = state?.model;
  const tradeInValue = state?.tradeInValue ?? 0;

  const [selectedRetailer, setSelectedRetailer] = useState<string>("bestbuy");
  const [selectedDelivery, setSelectedDelivery] = useState<string>("standard");
  const [addProtection, setAddProtection] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [taxRate, setTaxRate] = useState(0.08);

  useEffect(() => {
    getBusinessRules()
      .then((rules) => {
        if (rules?.taxRate) setTaxRate(rules.taxRate);
      })
      .catch(() => {
        /* use 0.08 fallback */
      });
  }, []);

  const handlePaymentSuccess = (paymentDetails: PaymentDetails) => {
    console.log("Payment successful:", paymentDetails);
    setPaymentDrawerOpen(false);
    if (model) {
      navigate(`/assets/${id}`, {
        state: { purchaseSuccess: true, modelPurchased: model.name },
      });
    }
  };

  if (!model) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <p className="text-sm text-muted-foreground">{t('checkout.noModelSelected')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/assets/${id}/upgrade/browse`)}
        >
          {t('checkout.backToBrowse')}
        </Button>
      </div>
    );
  }

  const effectivePrice = Math.max(0, model.price - tradeInValue);
  const rebateDiscount = model.rebateEligible ? (model.rebateAmount ?? 0) : 0;
  const protectionPlanCost = addProtection ? 149 : 0;
  const deliveryFee = selectedDelivery === "express" ? 79 : 0;
  const subtotalBeforeTax =
    effectivePrice - rebateDiscount + protectionPlanCost + deliveryFee;
  const tax = +(subtotalBeforeTax * taxRate).toFixed(2);
  const totalPrice = subtotalBeforeTax + tax;

  const retailers = [
    {
      id: "bestbuy",
      name: "Best Buy",
      abbr: "BB",
      inStock: true,
      financing: "0% APR for 18 months",
      delivery: "Free delivery & haul-away",
    },
    {
      id: "homedepot",
      name: "Home Depot",
      abbr: "HD",
      inStock: true,
      financing: "0% APR for 12 months",
      delivery: "Free delivery",
    },
    {
      id: "lowes",
      name: "Lowe's",
      abbr: "LW",
      inStock: false,
      financing: "Special financing available",
      delivery: "Free delivery & installation",
    },
  ];

  const selectedRetailerData = retailers.find((r) => r.id === selectedRetailer);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur shadow-md">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-lg hover:bg-secondary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> {t('checkout.checkout')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Checkout steps */}
          <div className="lg:col-span-2 space-y-6">
            {/* Retailer */}
            <section className="rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  1
                </span>
                <h2 className="text-sm font-semibold">{t('checkout.selectRetailer')}</h2>
              </div>
              <div className="space-y-2">
                {retailers.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => r.inStock && setSelectedRetailer(r.id)}
                    disabled={!r.inStock}
                    className={cn(
                      "w-full rounded-lg border p-4 text-left transition-colors",
                      selectedRetailer === r.id
                        ? "border-foreground bg-secondary/40"
                        : "hover:bg-secondary/20",
                      !r.inStock && "opacity-40 cursor-not-allowed",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {r.abbr}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {r.name}
                          </span>
                          {r.inStock ? (
                            <span className="text-[10px] font-medium text-muted-foreground">
                              {t('checkout.inStock')}
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-muted-foreground">
                              {t('checkout.outOfStock')}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {r.financing} · {r.delivery}
                        </p>
                      </div>
                      {selectedRetailer === r.id && (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* Delivery */}
            <section className="rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  2
                </span>
                <h2 className="text-sm font-semibold">{t('checkout.delivery')}</h2>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedDelivery("standard")}
                  className={cn(
                    "w-full rounded-lg border p-4 text-left transition-colors",
                    selectedDelivery === "standard"
                      ? "border-foreground bg-secondary/40"
                      : "hover:bg-secondary/20",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-semibold">
                          {t('checkout.standardDelivery')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('checkout.standardDeliveryDesc')}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold">{t('checkout.free')}</span>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedDelivery("express")}
                  className={cn(
                    "w-full rounded-lg border p-4 text-left transition-colors",
                    selectedDelivery === "express"
                      ? "border-foreground bg-secondary/40"
                      : "hover:bg-secondary/20",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-semibold">
                          {t('checkout.expressDelivery')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('checkout.expressDeliveryDesc')}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold">
                      {formatCurrency(79)}
                    </span>
                  </div>
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {t('checkout.installationNote')}
              </p>
            </section>

            {/* Protection */}
            <section className="rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  3
                </span>
                <h2 className="text-sm font-semibold">{t('checkout.protectionPlan')}</h2>
              </div>
              <button
                onClick={() => setAddProtection(!addProtection)}
                className={cn(
                  "w-full rounded-lg border p-4 text-left transition-colors",
                  addProtection
                    ? "border-foreground bg-secondary/40"
                    : "hover:bg-secondary/20",
                )}
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">
                        {t('checkout.extendedWarranty3yr')}
                      </span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(149)}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {[
                        t('checkout.covers'),
                        t('checkout.support247'),
                        t('checkout.noDeductibles'),
                      ].map((item) => (
                        <li
                          key={item}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground"
                        >
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />{" "}
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {addProtection && (
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  )}
                </div>
              </button>
            </section>

            {/* Payment */}
            <section className="rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  4
                </span>
                <h2 className="text-sm font-semibold">{t('checkout.payment')}</h2>
              </div>
              <Button
                className="w-full"
                onClick={() => setPaymentDrawerOpen(true)}
              >
                <Lock className="h-3.5 w-3.5 mr-1.5" /> {t('checkout.proceedToPayment')}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                {t('checkout.encryptedNote')}
              </p>
            </section>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border p-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                {t('checkout.orderSummary')}
              </h3>

              {/* Product */}
              <div className="pb-4 mb-4 border-b border-dashed">
                <p className="text-sm font-semibold">
                  {model.brand} {model.name}
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  #{model.modelNumber}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={`star-${n}`}
                        className={cn(
                          "h-3 w-3",
                          n <= Math.floor(model.rating)
                            ? "fill-foreground text-foreground"
                            : "fill-muted text-muted",
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-medium">{model.rating}</span>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="space-y-2 pb-4 mb-4 border-b border-dashed text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('checkout.listPrice')}</span>
                  <span className="tabular-nums">
                    {formatCurrency(model.price)}
                  </span>
                </div>
                {tradeInValue > 0 && (
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Tag className="h-3 w-3" /> {t('checkout.tradeIn')}
                    </span>
                    <span className="font-medium tabular-nums">
                      −{formatCurrency(tradeInValue)}
                    </span>
                  </div>
                )}
                {rebateDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('checkout.rebate')}</span>
                    <span className="font-medium tabular-nums">
                      −{formatCurrency(rebateDiscount)}
                    </span>
                  </div>
                )}
                {deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('checkout.expressDeliveryLine')}
                    </span>
                    <span className="tabular-nums">
                      +{formatCurrency(deliveryFee)}
                    </span>
                  </div>
                )}
                {protectionPlanCost > 0 && (
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Shield className="h-3 w-3" /> {t('checkout.protection')}
                    </span>
                    <span className="tabular-nums">
                      +{formatCurrency(protectionPlanCost)}
                    </span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="flex justify-between items-baseline mb-4">
                <span className="text-sm font-semibold">{t('checkout.total')}</span>
                <div className="text-right">
                  <span className="text-xl font-semibold tabular-nums">
                    {formatCurrency(totalPrice)}
                  </span>
                  {totalPrice > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {t('checkout.monthlyFinancing', { amount: formatCurrency(Math.round(totalPrice / 24)) })}
                    </p>
                  )}
                </div>
              </div>

              {/* Financing note */}
              {selectedRetailerData && (
                <div className="rounded-lg border p-3 mb-3">
                  <div className="flex items-center gap-2 text-xs">
                    <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{t('checkout.financingAvailable')}</p>
                      <p className="text-muted-foreground">
                        {selectedRetailerData.financing}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Energy note */}
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">
                  {t('checkout.energyNote', { amount: formatCurrency(Math.round((model.annualEnergyCostEst ?? 0) * 0.25 * 5)) })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Drawer */}
      <PaymentDrawer
        isOpen={paymentDrawerOpen}
        onClose={() => setPaymentDrawerOpen(false)}
        orderSummary={{
          productName: `${model.brand} ${model.name}`,
          productPrice: model.price,
          tradeInValue: tradeInValue > 0 ? tradeInValue : undefined,
          rebateDiscount: rebateDiscount > 0 ? rebateDiscount : undefined,
          protectionPlan: addProtection
            ? { name: t('checkout.protectionPlanName'), price: protectionPlanCost }
            : undefined,
          shipping: deliveryFee,
          tax,
        }}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
}
